import {useEffect, useRef} from 'react';
import {
	useForm,
	type UseFormProps,
	type Path,
	type PathValue,
	type DefaultValues,
	type FieldValues,
} from 'react-hook-form';
import {type ErrorResult} from '@netx/core-next/lib/queryClient';
import {useSingleFileUploadProvider} from '../contexts/SingleFileUploadContext';
import {useConstCallback} from '@netx/core-components/hooks/useConstCallback';
import {useUpdateEffect} from '@netx/core-components/hooks/useUpdateEffect';

export interface SingleFileUploadFormValues extends FieldValues {
	fileName: string;
	fileUri: string;
}

export interface UseSingleFileUploadFormParams<
	TFieldValues extends SingleFileUploadFormValues = SingleFileUploadFormValues,
> {
	defaultValues?: DefaultValues<TFieldValues>;
	useFormProps?: UseFormProps<TFieldValues>;
	getErrorMessage?: (error: any | Error | ErrorResult) => string | undefined;
	submit: (data: TFieldValues) => Promise<void | unknown>;
}

export const useSingleFileUploadForm = <
	TFieldValues extends SingleFileUploadFormValues = SingleFileUploadFormValues,
>({
	defaultValues,
	useFormProps,
	getErrorMessage,
	submit,
}: UseSingleFileUploadFormParams<TFieldValues>) => {
	const uploadProvider = useSingleFileUploadProvider();
	const form = useForm<TFieldValues>({
		...useFormProps,
		defaultValues: {
			fileName: '',
			...useFormProps?.defaultValues,
			...defaultValues,
		} as DefaultValues<TFieldValues>,
	});

	const currentFileId = useRef<string | null>(null);

	useEffect(() => {
		// when files change, clear errors and revalidate the form
		const dispose = uploadProvider.uppyStore.subscribe(
			(prevState, nextState, patch) => {
				if (patch.files != null) {
					const fileId = Object.values(patch.files)[0]?.id ?? null;
					if (fileId === currentFileId.current) return;
					currentFileId.current = fileId;
					form.clearErrors('root');
					form.trigger('fileName' as Path<TFieldValues>);
				}
			},
		);
		return () => {
			dispose();
		};
	}, []);

	useUpdateEffect(() => {
		reset();
	}, [defaultValues]);

	/**
	 * Actual form submission that will be called after the upload has completed
	 * @private
	 */
	const formSubmit = form.handleSubmit(async (data) => {
		return submit(data).catch((error) => {
			form.setError('root', {
				message: getErrorMessage?.(error) ?? error.message,
				type: 'error',
			});
		});
	});

	/**
	 * We will initiate the upload on submit but not let the form itself know until the upload has completed
	 */
	const onSubmit = (event: React.FormEvent) => {
		event.preventDefault();
		uploadProvider.uppy
			.upload()
			.then((result) => {
				const successfuleFile = result.successful[0];
				if (!successfuleFile) {
					form.setError('root', {
						message: i18n.t('i18n.errorRequestFailed'),
						type: 'error',
					});
					return;
				}
				form.setValue(
					'fileName' as Path<TFieldValues>,
					successfuleFile.name as PathValue<TFieldValues, Path<TFieldValues>>,
				);
				form.setValue(
					'fileUri' as Path<TFieldValues>,
					successfuleFile.response?.uploadURL as PathValue<
						TFieldValues,
						Path<TFieldValues>
					>,
				);
				formSubmit(event);
			})
			.catch((error) => {
				form.setError('root', {
					message: i18n.t('i18n.errorRequestFailed'),
					type: 'error',
				});
			});
	};

	const reset = useConstCallback(() => {
		form.reset({
			fileName: '',
			...useFormProps?.defaultValues,
			...defaultValues,
		} as DefaultValues<TFieldValues>);
	});

	return {
		form,
		// pass to your form component
		onSubmit,
		reset,
	};
};

// Typescript cannot infer `ReturnType` of a generic, so you must roll your own
class InferWrapper<
	TFieldValues extends SingleFileUploadFormValues = SingleFileUploadFormValues,
> {
	mediate = (params: UseSingleFileUploadFormParams<TFieldValues>) => {
		return useSingleFileUploadForm<TFieldValues>(params);
	};
}

export type UseUploadType<
	TFieldValues extends SingleFileUploadFormValues = SingleFileUploadFormValues,
> = ReturnType<InferWrapper<TFieldValues>['mediate']>;
